import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BillingToggle from '../common/BillingToggle';
import { useAuth } from '../../context/AuthContext';
import { useRegisterAction } from '../../hooks/useRegisterAction';
import { fetchCatalogue, checkoutAndRedirect, itemKeyFor } from '../../services/stripeBilling';
import { METERS, allowanceFor, formatNumber } from '../../lib/meters';
import styles from './PricingTiers.module.css';

// Pricing-page wording for each meter row (distinct from the Account page's labels). The
// numbers come from the shared catalogue (lib/meters) so this page can never again disagree
// with the account page or what the backend actually grants.
const PRICING_ROW_LABEL = {
  verify:  'Verify emails / mo',
  enrich:  'Enrich companies / mo',
  website: 'Website lookups / mo',
  ai:      'AI email drafting + chat',
};
function planDetails(plan) {
  const rows = METERS
    .filter((m) => m.key !== 'leads') // leads render separately as the headline
    .map((m) => {
      const v = allowanceFor(m.key, plan);
      return { label: PRICING_ROW_LABEL[m.key], value: typeof v === 'number' ? formatNumber(v) : v };
    });
  return { leads: `${formatNumber(allowanceFor('leads', plan))} verified leads / mo`, rows };
}

const TIERS = [
  {
    name: 'Solo',
    annual: 49,
    monthly: 59,
    unit: '/mo',
    seatNote: null,
    featured: false,
    ...planDetails('Solo'),
    extras: null,
  },
  {
    name: 'Pro',
    annual: 99,
    monthly: 119,
    unit: '/mo',
    seatNote: null,
    featured: true,
    ...planDetails('Pro'),
    extras: 'Competitor discovery, buying-signal hunts, priority support',
  },
  {
    name: 'Team',
    annual: 99,
    monthly: 119,
    unit: '/user/mo',
    seatNote: '3-seat minimum',
    featured: false,
    comingSoon: true,
    // Matches Pro's credits/usage until the Team plan is finalised.
    ...planDetails('Pro'),
    extras: 'Everything in Pro, plus shared workspace and team-wide dedup',
  },
];

export default function PricingTiers() {
  // Monthly by default. Landing on the annual price makes the headline number look like the
  // monthly commitment and surprises people at checkout; monthly is the honest default and
  // the toggle advertises the annual saving right next to it.
  const [billing, setBilling] = useState('monthly'); // 'annual' | 'monthly'
  const register = useRegisterAction();
  const { user, getToken } = useAuth();
  const navigate = useNavigate();

  const [catalogue, setCatalogue] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const c = await fetchCatalogue({ token: getToken?.() });
      if (alive) setCatalogue(c);
    })();
    return () => { alive = false; };
    // getToken is deliberately not a dependency: it is not referentially stable, and the
    // token is re-read on every run anyway. Mirrors useAccessStatus.
  }, [user?.id]);

  // ── Displayed price ─────────────────────────────────────────────────────────
  // Derived from the real Stripe amount, never from the hardcoded tier numbers, which had
  // drifted badly: the card advertised "$49/mo billed annually" (ie $588/yr) while Stripe
  // charges $490/yr. Quoting an annual plan as a per-month figure is fine, but the figure
  // has to be the yearly price divided by twelve or it is simply wrong, and in this case it
  // understated the discount by about $98 a year on Solo and $198 on Pro.
  //
  // Falls back to the tier's static number when the catalogue cannot be read, so the
  // marketing page still renders.
  function displayPrice(t) {
    const yearly = catalogue?.items?.[itemKeyFor(t.name, 'yearly')];
    const monthly = catalogue?.items?.[itemKeyFor(t.name, 'monthly')];
    if (billing === 'annual') {
      if (!yearly) return { amount: t.annual, yearlyTotal: null };
      return { amount: Math.round(yearly.amountCents / 1200), yearlyTotal: yearly.amountCents / 100 };
    }
    if (!monthly) return { amount: t.monthly, yearlyTotal: null };
    return { amount: Math.round(monthly.amountCents / 100), yearlyTotal: null };
  }

  const lockedBook = catalogue?.priceBook?.lifetimeLock ? catalogue.priceBook.label : null;

  // How much annual actually saves, from the live prices. Returns whole months free (floored
  // so we never overstate) plus the percentage. Solo: $490 against 12 x $59 is 3 months free
  // and 31% off. Founding Solo: $290 against 12 x $29 is exactly 2 months free.
  function annualSaving(planName) {
    const m = catalogue?.items?.[itemKeyFor(planName, 'monthly')];
    const y = catalogue?.items?.[itemKeyFor(planName, 'yearly')];
    if (!m?.amountCents || !y?.amountCents) return null;
    const monthsFree = Math.floor(12 - y.amountCents / m.amountCents);
    const pct = Math.round((1 - y.amountCents / (m.amountCents * 12)) * 100);
    return { monthsFree, pct };
  }
  // The toggle badge quotes the smallest saving on offer, so it is true for every plan shown.
  const toggleSaving = (() => {
    const all = ['Solo', 'Pro'].map(annualSaving).filter(Boolean);
    if (!all.length) return null;
    return `save ${Math.min(...all.map((a) => a.pct))}%`;
  })();

  async function choose(t) {
    // Guests sign up first; the paywall then offers checkout at the end of their trial.
    if (!user) { register.go(); return; }
    const itemKey = itemKeyFor(t.name, billing === 'annual' ? 'yearly' : 'monthly');
    setBusy(itemKey);
    setError(null);
    try {
      await checkoutAndRedirect(itemKey, { token: getToken?.() });
    } catch (err) {
      // Plan changes belong in the Portal, where Stripe prorates. A second Checkout would
      // create a second subscription and bill them twice.
      if (err.code === 'already_subscribed') { navigate('/account?tab=billing'); return; }
      setError(err.message || 'Could not start checkout. Please try again.');
      setBusy(null);
    }
  }

  const billingNote = billing === 'annual' ? 'billed annually' : 'billed monthly';

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.head}>
          <span className={styles.label}>Pricing</span>
          <h1 className={styles.heading}>Pricing that only charges you for new leads.</h1>
          <p className={styles.sub}>
            Credits buy one thing: net-new, verified leads. Everything else (verifying old
            lists, enriching companies, drafting emails, chatting with your agents) is
            included in your plan.
          </p>
        </div>

        {/* Billing toggle */}
        <BillingToggle billing={billing} onChange={setBilling} className={styles.toggleSpacing} savingLabel={toggleSaving} />

        {/* Tier cards */}
        <div className={styles.cards}>
          {TIERS.map((t) => (
            <div
              key={t.name}
              className={`${styles.card} ${t.featured ? styles.featured : ''} ${t.comingSoon ? styles.comingSoon : ''}`}
            >
              {t.featured && <span className={styles.badge}>Most popular</span>}
              {t.comingSoon && <span className={styles.badgeSoon}>Coming soon</span>}

              <h2 className={styles.tierName}>{t.name}</h2>

              <div className={styles.priceRow}>
                {t.comingSoon ? (
                  <span className={styles.price}>TBD</span>
                ) : (
                  <>
                    <span className={styles.price}>${displayPrice(t).amount}</span>
                    <span className={styles.unit}>{t.unit}</span>
                  </>
                )}
              </div>
              <span className={styles.priceNote}>
                {(() => {
                  const { yearlyTotal } = displayPrice(t);
                  const base = t.seatNote ? `${t.seatNote} · ${billingNote}` : billingNote;
                  // State the real yearly charge next to the per-month figure, so the
                  // number on the card and the number on the invoice agree.
                  return yearlyTotal ? `${base} · $${yearlyTotal.toLocaleString('en-US')}/yr` : base;
                })()}
              </span>

              {/* The annual incentive, stated on every card and computed from the real
                  prices so it cannot drift from what Stripe charges. Shown on the monthly
                  view too (where it is the reason to switch) and on the annual view (where
                  it is the reason they were right to). */}
              {!t.comingSoon && annualSaving(t.name)?.monthsFree >= 1 && (
                <span className={styles.yearlyPerk}>
                  + {annualSaving(t.name).monthsFree} months free when paid yearly
                </span>
              )}

              <div className={styles.leads}>{t.leads}</div>

              <ul className={styles.rows}>
                {t.rows.map((r) => (
                  <li key={r.label} className={styles.row}>
                    <span className={styles.rowLabel}>{r.label}</span>
                    <span className={styles.rowValue}>{r.value}</span>
                  </li>
                ))}
              </ul>

              {t.extras && (
                <div className={styles.extras}>
                  <span className={styles.extrasLabel}>Extras</span>
                  <p className={styles.extrasText}>{t.extras}</p>
                </div>
              )}

              {t.comingSoon ? (
                <button className={styles.ctaDisabled} disabled>
                  Coming soon
                </button>
              ) : (
                <button
                  className={t.featured ? styles.ctaPrimary : styles.ctaGhost}
                  onClick={() => choose(t)}
                  disabled={busy !== null}
                >
                  {busy === itemKeyFor(t.name, billing === 'annual' ? 'yearly' : 'monthly')
                    ? 'Opening checkout…'
                    : (user ? `Choose ${t.name}` : register.label)}
                </button>
              )}
            </div>
          ))}
        </div>

        {lockedBook && (
          <p className={styles.sub} style={{ marginTop: 18 }}>
            🔒 You're on <b>{lockedBook}</b> pricing. These are your locked rates.
          </p>
        )}
        {error && (
          <p className={styles.sub} role="alert" style={{ marginTop: 18, color: 'var(--danger, #e5484d)' }}>
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
